import { useContext } from 'react';

import { Anchor, Heading, List, Modal, Text } from '@zillow/constellation-10';

import { RMWContext } from '../../../providers/RMWContext';

import { useSelector } from '../../../selectors/useSelector';

import propertyUtils from '../../../utils/propertyUtils';

import ListingActivationConstants from '../../../constants/ListingActivationConstants';

type Props = {
  error: {
    code?: string;
    message?: string;
    data?: Record<string, unknown>;
  };
  onClose: () => void;
};

const ErrorModal = ({ error, onClose }: Props) => {
  const { paths } = useContext(RMWContext);

  const { street, city, state, zip, unit } = useSelector((state) => state.properties.currentProperty);
  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    street,
    city,
    state,
    zip,
  });

  const supportLink = `${paths.submitRequest}?category=myListing&topic=unableToPublish&listingAddress=${formattedCompleteAddress}&unit=${unit}`;

  let errorMessage;

  let errorItems = [];

  switch (error.code) {
    case ListingActivationConstants.ERROR_CODE.INVALID_FIELDS:
      errorMessage = 'We found some invalid fields while activating your listing.';
      if (error.data?.listing) {
        errorItems = Object.values(error.data.listing);
      }
      if (error.data?.floorPlans) {
        errorItems.push('Fix the errors in your floor plans.');
      }
      break;
    case ListingActivationConstants.ERROR_CODE.LISTING_BLOCKED:
    case ListingActivationConstants.ERROR_CODE.CONTACT_BLOCKED:
      errorMessage = (
        <span>
          We&apos;re unable to activate this listing at this time.
          <Anchor href={supportLink}>Contact</Anchor>
          &nbsp;our team to resolve the issue.
        </span>
      );
      break;
    default:
      errorMessage = error.message;
  }

  return (
    <Modal
      header={<Heading level={2}>Error</Heading>}
      body={
        <>
          <Text>{errorMessage}</Text>
          {errorItems.length > 0 && (
            <List.Root>
              {errorItems.map((errorItem) => (
                <List.Item key={errorItem}>{errorItem}</List.Item>
              ))}
            </List.Root>
          )}
        </>
      }
      onOpenChange={(open) => !open && onClose()}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export { ErrorModal };
export default ErrorModal;
